import styled, { x } from '@xstyled/styled-components';
import React, { FC, useEffect, useState, Fragment } from 'react';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import {
  QuizMemoryGameStore,
  useQuizMemoryGameStore
} from '../MemoryGameStore';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import audioEffectRightAnswer from '~/assets/mp3/right_answer.mp3';
import audioEffectWrongAnswer from '~/assets/mp3/wrong_answer.mp3';
import Confetti from 'react-confetti';
import useWindowSize from 'react-use/lib/useWindowSize';

import BottomLlamaImg from './WellDoneLlama.svg';
import MatchedImg from './matched.svg';
import ArrowDubble from '~/assets/icons/arrow.bubble.svg';
import { t } from 'ttag';
import { Button } from '~/ui/buttons';
import { useEditorStore } from '~/shell/Store';

const selector = (s: QuizMemoryGameStore) =>
  select(s, 'termsItems', 'descriptionItems');
const sequenceSelector = (s: SequenceStore) =>
  select(s, 'addBlocker', 'removeBlocker', 'blockers');

const MemoryCardTitle = styled.divBox`
  color: var(--purple-primary, #9845fb);
  font-family: Raleway;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 1.4px;
  text-transform: uppercase;
`;

export const MemoryCard = styled.divBox<{
  correctBorderColor?: string;
  tabIndex?: number;
}>`
  z-index: 4;
  width: 93%;
  height: 200px;
  background-color: #fff;
  color: #000;
  margin-bottom: 10px;
  border-radius: 20px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  box-shadow:
    0px 15px 20px 0px rgba(0, 0, 0, 0.05),
    0px 30px 30px 0px rgba(0, 0, 0, 0.03),
    0px -2px 4px 0px rgba(0, 0, 0, 0.05);
  display: block;
  justify-content: center;
  align-items: center;
  border: 5px solid #fff;
  overflow: -moz-scrollbars-vertical;
  overflow-y: scroll;

  @media (max-width: md) {
    padding: 10px 5px;
  }

  div {
    width: 100%;
  }
  h2 {
    display: block;
  }
  &.active {
    border: 5px solid var(--purple-med, #b580f9);
  }
  &.correct {
    border: 5px solid ${p => p.correctBorderColor || '#00A88A'};
    cursor: auto;
  }
  &.wrong {
    border: 5px solid var(--alt-red-dark, #f7326d);
    -webkit-animation: shake-lr 0.7s cubic-bezier(0.455, 0.03, 0.515, 0.955)
      both;
    animation: shake-lr 0.7s cubic-bezier(0.455, 0.03, 0.515, 0.955) both;
  }
  @-webkit-keyframes shake-lr {
    0%,
    100% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
      -webkit-transform-origin: 50% 50%;
      transform-origin: 50% 50%;
    }
    10% {
      -webkit-transform: rotate(8deg);
      transform: rotate(8deg);
    }
    20%,
    40%,
    60% {
      -webkit-transform: rotate(-10deg);
      transform: rotate(-10deg);
    }
    30%,
    50%,
    70% {
      -webkit-transform: rotate(10deg);
      transform: rotate(10deg);
    }
    80% {
      -webkit-transform: rotate(-8deg);
      transform: rotate(-8deg);
    }
    90% {
      -webkit-transform: rotate(8deg);
      transform: rotate(8deg);
    }
  }
  @keyframes shake-lr {
    0%,
    100% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
      -webkit-transform-origin: 50% 50%;
      transform-origin: 50% 50%;
    }
    10% {
      -webkit-transform: rotate(8deg);
      transform: rotate(8deg);
    }
    20%,
    40%,
    60% {
      -webkit-transform: rotate(-10deg);
      transform: rotate(-10deg);
    }
    30%,
    50%,
    70% {
      -webkit-transform: rotate(10deg);
      transform: rotate(10deg);
    }
    80% {
      -webkit-transform: rotate(-8deg);
      transform: rotate(-8deg);
    }
    90% {
      -webkit-transform: rotate(8deg);
      transform: rotate(8deg);
    }
  }
  @-webkit-keyframes shake-bottom {
    0%,
    100% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
      -webkit-transform-origin: 50% 100%;
      transform-origin: 50% 100%;
    }
    10% {
      -webkit-transform: rotate(2deg);
      transform: rotate(2deg);
    }
    20%,
    40%,
    60% {
      -webkit-transform: rotate(-4deg);
      transform: rotate(-4deg);
    }
    30%,
    50%,
    70% {
      -webkit-transform: rotate(4deg);
      transform: rotate(4deg);
    }
    80% {
      -webkit-transform: rotate(-2deg);
      transform: rotate(-2deg);
    }
    90% {
      -webkit-transform: rotate(2deg);
      transform: rotate(2deg);
    }
  }
  @keyframes shake-bottom {
    0%,
    100% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
      -webkit-transform-origin: 50% 100%;
      transform-origin: 50% 100%;
    }
    10% {
      -webkit-transform: rotate(2deg);
      transform: rotate(2deg);
    }
    20%,
    40%,
    60% {
      -webkit-transform: rotate(-4deg);
      transform: rotate(-4deg);
    }
    30%,
    50%,
    70% {
      -webkit-transform: rotate(4deg);
      transform: rotate(4deg);
    }
    80% {
      -webkit-transform: rotate(-2deg);
      transform: rotate(-2deg);
    }
    90% {
      -webkit-transform: rotate(2deg);
      transform: rotate(2deg);
    }
  }
`;

const Matched = styled(MatchedImg)`
  position: relative;
  margin-top: -185px;
  margin-left: 220px;

  -webkit-animation: puff-out-center 3s cubic-bezier(0.165, 0.84, 0.44, 1) both;
  animation: puff-out-center 3s cubic-bezier(0.165, 0.84, 0.44, 1) both;

  @-webkit-keyframes puff-out-center {
    0% {
      -webkit-transform: scale(1);
      transform: scale(1);
      -webkit-filter: blur(0px);
      filter: blur(0px);
      opacity: 1;
    }
    100% {
      -webkit-transform: scale(2);
      transform: scale(2);
      -webkit-filter: blur(4px);
      filter: blur(4px);
      opacity: 0;
    }
  }
  @keyframes puff-out-center {
    0% {
      -webkit-transform: scale(1);
      transform: scale(1);
      -webkit-filter: blur(0px);
      filter: blur(0px);
      opacity: 1;
    }
    100% {
      -webkit-transform: scale(2);
      transform: scale(2);
      -webkit-filter: blur(4px);
      filter: blur(4px);
      opacity: 0;
    }
  }
`;

const WellDoneLlama = styled(BottomLlamaImg)`
  align-self: last baseline;
  z-index: 3;
  margin-left: -200px;

  -webkit-animation: slide-in-bottom 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)
    0.5s both;
  animation: slide-in-bottom 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.5s both;

  @-webkit-keyframes slide-in-bottom {
    0% {
      -webkit-transform: translateY(1000px);
      transform: translateY(1000px);
      opacity: 0;
    }
    100% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      opacity: 1;
    }
  }
  @keyframes slide-in-bottom {
    0% {
      -webkit-transform: translateY(1000px);
      transform: translateY(1000px);
      opacity: 0;
    }
    100% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const WellDoneWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  overflow: hidden;

  .wellDoneUnion {
    color: #000;
  }

  @media (max-width: sm) {
    .wellDoneWrapper {
      display: block;
      width: 85%;
      margin-left: auto;
      .wellDoneUnion {
        width: 100%;
        margin-top: -100px;
        margin-left: auto;
        margin-right: auto;
      }
    }
  }
`;

const Wrapper = styled.div`
  position: absolute;
  z-index: 10;

  width: 250px;
  height: 271px;
  top: 35%;
  margin-left: 40%;

  .arrowRight {
    transform: rotate(360deg);
    position: absolute;

    margin-left: -150px;
    margin-top: 166px;
  }
  h2 {
    font-family: var(--llama-font);
    font-size: 24px;
    text-align: center;
    padding-bottom: 20px;
  }
  @media (max-width: sm) {
    .arrowRight {
      display: none;
    }
  }

  -webkit-animation: bounce-in-top 2s both;
  animation: bounce-in-top 2s both;

  @-webkit-keyframes bounce-in-top {
    0% {
      -webkit-transform: translateY(-500px);
      transform: translateY(-500px);
      -webkit-animation-timing-function: ease-in;
      animation-timing-function: ease-in;
      opacity: 0;
    }
    38% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      -webkit-animation-timing-function: ease-out;
      animation-timing-function: ease-out;
      opacity: 1;
    }
    55% {
      -webkit-transform: translateY(-65px);
      transform: translateY(-65px);
      -webkit-animation-timing-function: ease-in;
      animation-timing-function: ease-in;
    }
    72% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      -webkit-animation-timing-function: ease-out;
      animation-timing-function: ease-out;
    }
    81% {
      -webkit-transform: translateY(-28px);
      transform: translateY(-28px);
      -webkit-animation-timing-function: ease-in;
      animation-timing-function: ease-in;
    }
    90% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      -webkit-animation-timing-function: ease-out;
      animation-timing-function: ease-out;
    }
    95% {
      -webkit-transform: translateY(-8px);
      transform: translateY(-8px);
      -webkit-animation-timing-function: ease-in;
      animation-timing-function: ease-in;
    }
    100% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      -webkit-animation-timing-function: ease-out;
      animation-timing-function: ease-out;
    }
  }
  @keyframes bounce-in-top {
    0% {
      -webkit-transform: translateY(-500px);
      transform: translateY(-500px);
      -webkit-animation-timing-function: ease-in;
      animation-timing-function: ease-in;
      opacity: 0;
    }
    38% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      -webkit-animation-timing-function: ease-out;
      animation-timing-function: ease-out;
      opacity: 1;
    }
    55% {
      -webkit-transform: translateY(-65px);
      transform: translateY(-65px);
      -webkit-animation-timing-function: ease-in;
      animation-timing-function: ease-in;
    }
    72% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      -webkit-animation-timing-function: ease-out;
      animation-timing-function: ease-out;
    }
    81% {
      -webkit-transform: translateY(-28px);
      transform: translateY(-28px);
      -webkit-animation-timing-function: ease-in;
      animation-timing-function: ease-in;
    }
    90% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      -webkit-animation-timing-function: ease-out;
      animation-timing-function: ease-out;
    }
    95% {
      -webkit-transform: translateY(-8px);
      transform: translateY(-8px);
      -webkit-animation-timing-function: ease-in;
      animation-timing-function: ease-in;
    }
    100% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      -webkit-animation-timing-function: ease-out;
      animation-timing-function: ease-out;
    }
  }
`;

const Union = styled.div`
  width: 100%;
  min-height: 130px;
  background-color: #fff;
  border-radius: 15px;
  padding: 20px;
  position: absolute;
  margin-left: -180px;
  box-shadow: -10px -10px 20px grey;
  max-height: 100%;
  overflow-x: auto;

  .container {
    position: relative;
    width: 85%;
    height: 0;
    padding-bottom: 40%;
    margin-left: auto;
    margin-right: auto;
  }
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  button {
    margin-left: auto;
    margin-right: auto;
    display: block;
  }
  @media (max-width: sm) {
    margin-left: -185px;
    width: 365px;
    margin-top: -300px;
  }
`;

const Overlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 100;
`;

const WellDone = React.memo(() => {
  const { width, height } = useWindowSize();
  const [showLlama, setShowLlama] = useState(true);

  if (!showLlama) {
    return null;
  }

  return (
    <Overlay>
      <Confetti width={width} height={height} />
      <WellDoneWrapper>
        <WellDoneLlama />
        <Wrapper className="wellDoneWrapper">
          <ArrowDubble className="arrowRight" />
          <Union className="wellDoneUnion">
            <h2>{t`Well done!`} 🥳</h2>
            <div className="bubbleContent">
              <h2>{t`Select the Close button to review your matches.`}</h2>
            </div>
            <Button onClick={() => setShowLlama(false)}>{t`Close`}</Button>
          </Union>
        </Wrapper>
      </WellDoneWrapper>
    </Overlay>
  );
});

interface TermItem {
  pairId: string;
  boxType?: string;
  label: string;
  selected: boolean;
  matched: boolean;
  wrong: boolean;
}

export const MemoryGameRoot: FC<{ id: string }> = ({ id }) => {
  const { editorMode, contentEditable, isReadOnlyView } = useEditorStore();
  const [feedback, setFeedback] = useState<string | null>(null);
  const [itemList, setItemList] = useState<Array<TermItem> | null>(null);
  const [selectedItems, setSelectedItems] = useState<{
    pairId: string;
    boxType?: string;
    label: string;
    selected: boolean;
    matched: boolean;
    wrong: boolean;
  } | null>(null);

  const { termsItems, descriptionItems } = useQuizMemoryGameStore(
    selector,
    shallow
  );

  const { addBlocker, removeBlocker, blockers } = useSequenceStore(
    sequenceSelector,
    shallow
  );

  const [playAudioEffectRightAnswer] = useAudioEffect(audioEffectRightAnswer);
  const [playAudioEffectWrongAnswer] = useAudioEffect(audioEffectWrongAnswer);

  useEffect(() => {
    if (termsItems.length === 0) return;

    termsItems.forEach(i => addBlocker(i.pairId));
    return () => {
      termsItems.forEach(i => removeBlocker(i.pairId));
    };
  }, [termsItems.length]);

  const items: TermItem[] = [];

  termsItems.forEach(i =>
    items.push({
      ...i,
      boxType: 'TERM',
      selected: false,
      matched: false,
      wrong: false
    })
  );

  descriptionItems.forEach(i =>
    items.push({
      ...i,
      boxType: 'DESCRIPTION',
      selected: false,
      matched: false,
      wrong: false
    })
  );

  useEffect(
    () =>
      setItemList(
        items.sort((a: TermItem, b: TermItem) => 0.5 - Math.random())
      ),
    []
  );

  // Show content directly in read-only mode
  if (isReadOnlyView) {
    const readOnlyItems: TermItem[] = [];
    const colors = ['#00A88A', '#9845fb'];
    const pairColorMap = new Map();

    termsItems.forEach((i, index) => {
      pairColorMap.set(i.pairId, colors[index % colors.length]);
    });

    termsItems.forEach(i =>
      readOnlyItems.push({
        ...i,
        boxType: 'TERM',
        matched: true,
        selected: false,
        wrong: false
      })
    );

    descriptionItems.forEach(i =>
      readOnlyItems.push({
        ...i,
        boxType: 'DESCRIPTION',
        matched: true,
        selected: false,
        wrong: false
      })
    );

    return (
      <x.div
        w="100%"
        h="100%"
        p="xxl"
        display="grid"
        gridTemplateColumns={{ md: '1fr 1fr 1fr', _: '1fr 1fr' }}
      >
        {readOnlyItems.map((i: TermItem) => {
          const key = `${i.pairId}_${i.boxType}`;
          const borderColor = pairColorMap.get(i.pairId);
          return (
            <div key={`div_${key}`}>
              <MemoryCard
                as="div"
                key={key}
                className="correct"
                correctBorderColor={borderColor}
              >
                <Fragment key={`fr_${key}`}>
                  <MemoryCardTitle>{i?.boxType}</MemoryCardTitle>
                  <h2>{i?.label}</h2>
                </Fragment>
              </MemoryCard>
            </div>
          );
        })}
      </x.div>
    );
  }

  const removeWrongStatus = () => {
    const newList = itemList?.map(i => {
      i.wrong = false;
      i.selected = false;
      return i;
    });

    setItemList(newList as [TermItem]);
    setSelectedItems(null);
  };

  const onClick = (item: TermItem) => {
    setFeedback(null);

    if (item.matched) {
      return false;
    }

    if (
      selectedItems &&
      selectedItems?.pairId == item?.pairId &&
      selectedItems?.boxType !== item?.boxType
    ) {
      const newList = itemList?.map(i => {
        if (i.pairId == item.pairId) {
          i.matched = true;
        }
        return i;
      });

      removeBlocker(item?.pairId);
      setItemList(newList as [TermItem]);
      setSelectedItems(null);
      // Don't play audio effects in read-only mode
      if (!isReadOnlyView) {
        playAudioEffectRightAnswer();
      }
      setFeedback(t`Correct`);
    } else {
      if (
        selectedItems &&
        selectedItems?.pairId == item?.pairId &&
        selectedItems?.boxType == item?.boxType
      ) {
        //need to remove (it is uncheck)
        setSelectedItems(null);
      } else if (
        selectedItems &&
        selectedItems?.pairId &&
        selectedItems?.pairId != item?.pairId
      ) {
        //wrong need to set wrong class name

        let isWrong = false;
        const newList = itemList?.map(i => {
          if (
            (i?.pairId == item?.pairId && i?.boxType == item?.boxType) ||
            (i?.pairId == selectedItems?.pairId &&
              i?.boxType === selectedItems.boxType)
          ) {
            i.matched = false;
            i.wrong = true;
            isWrong = true;
          }
          return i;
        });

        setItemList(newList as [TermItem]);
        setSelectedItems(null);
        setTimeout(removeWrongStatus, 1500);

        setFeedback(t`Incorrect`);

        // Don't play audio effects in read-only mode
        if (isWrong && !isReadOnlyView) {
          playAudioEffectWrongAnswer();
        }
      } else {
        setSelectedItems(item);
      }

      const newList = itemList?.map(i => {
        if (i?.pairId == item?.pairId && i?.boxType == item?.boxType) {
          i.selected = !i.selected;
        }
        return i;
      });

      setItemList(newList as [TermItem]);
    }
  };

  const getClassName = (i: TermItem) => {
    if (i?.wrong) {
      return 'wrong';
    }

    if (i?.matched) {
      return 'correct';
    }

    return i?.selected ? 'active' : '';
  };

  return (
    <x.div
      w="100%"
      h="100%"
      p="xxl"
      display="grid"
      gridTemplateColumns={{ md: '1fr 1fr 1fr', _: '1fr 1fr' }}
    >
      {feedback ? (
        <span className="sr-only" aria-live="polite">
          {feedback}
        </span>
      ) : (
        false
      )}
      {itemList?.map(i => {
        const key = `${i.pairId}_${i.boxType}`;
        return (
          <div key={`div_${key}`}>
            <MemoryCard
              as="button"
              key={key}
              {...i}
              className={getClassName(i)}
              onClick={() => onClick(i)}
              role="switch"
              aria-checked={i?.selected ? 'true' : 'false'}
            >
              <Fragment key={`fr_${key}`}>
                <MemoryCardTitle>{i?.boxType}</MemoryCardTitle>
                <h2>{i?.label}</h2>
              </Fragment>
            </MemoryCard>
            {i?.matched && <Matched />}
          </div>
        );
      })}
      {blockers.length == 0 && <WellDone />}
    </x.div>
  );
};
